/**
 * PDF Blob utilities for frontend PDF generation and transmission
 * Handles React-PDF blob creation and conversion for backend communication
 */

import { pdf } from '@react-pdf/renderer'
import { createElement } from 'react'

/**
 * Convert PDF blob to base64 for JSON transmission
 * @param {Blob} pdfBlob - PDF blob from React-PDF
 * @returns {Promise<string>} - Base64 encoded PDF
 */
export const convertBlobToBase64 = (pdfBlob) => {
  return new Promise((resolve, reject) => {
    if (!pdfBlob || !(pdfBlob instanceof Blob)) {
      reject(new Error('Invalid blob provided'))
      return
    }

    console.log('🔄 Converting blob to base64...')
    console.log('🔍 Blob details:', {
      size: pdfBlob.size,
      type: pdfBlob.type,
      constructor: pdfBlob.constructor.name
    })

    const reader = new FileReader()
    
    reader.onloadend = () => {
      try {
        const result = reader.result
        if (typeof result === 'string') {
          console.log('✅ Blob converted to base64')
          console.log('🔍 Base64 length:', result.length)
          console.log('🔍 Base64 prefix:', result.substring(0, 50))
          resolve(result)
        } else {
          reject(new Error('Failed to convert blob to base64 - result is not a string'))
        }
      } catch (error) {
        console.error('❌ Blob conversion error:', error)
        reject(new Error(`Blob conversion error: ${error.message}`))
      }
    }
    
    reader.onerror = (error) => {
      console.error('❌ FileReader error:', error)
      reject(new Error('Failed to read blob'))
    }
    
    // Use readAsDataURL to get data:application/pdf;base64,... format
    reader.readAsDataURL(pdfBlob)
  })
}

/**
 * Alternative method: Convert blob to ArrayBuffer then to base64
 * @param {Blob} pdfBlob - PDF blob from React-PDF
 * @returns {Promise<string>} - Base64 encoded PDF with data URL prefix
 */
export const convertBlobToBase64Alternative = (pdfBlob) => {
  return new Promise((resolve, reject) => {
    if (!pdfBlob || !(pdfBlob instanceof Blob)) {
      reject(new Error('Invalid blob provided'))
      return
    }

    console.log('🔄 Converting blob via ArrayBuffer method...')
    
    const reader = new FileReader()
    
    reader.onloadend = () => {
      try {
        const arrayBuffer = reader.result
        const uint8Array = new Uint8Array(arrayBuffer)
        
        // Convert to base64
        let binary = ''
        for (let i = 0; i < uint8Array.length; i++) {
          binary += String.fromCharCode(uint8Array[i])
        }
        
        const base64 = btoa(binary)
        const dataUrl = `data:application/pdf;base64,${base64}`
        
        console.log('✅ Alternative conversion successful')
        console.log('🔍 Result length:', dataUrl.length)
        resolve(dataUrl)
        
      } catch (error) {
        console.error('❌ Alternative conversion error:', error)
        reject(new Error(`Alternative conversion error: ${error.message}`))
      }
    }
    
    reader.onerror = (error) => {
      console.error('❌ ArrayBuffer reader error:', error)
      reject(new Error('Failed to read blob as ArrayBuffer'))
    }
    
    reader.readAsArrayBuffer(pdfBlob)
  })
}

/**
 * Generate PDF blob from React component and convert to base64
 * @param {React.Component} pdfComponent - React-PDF component
 * @returns {Promise<Object>} - Object with blob and base64 data
 */
export const generatePdfBlobAndBase64 = async (pdfComponent) => {
  try {
    console.log('📄 Génération PDF blob...')
    
    // Generate PDF blob
    const pdfBlob = await pdf(pdfComponent).toBlob()
    
    if (!pdfBlob || pdfBlob.size === 0) {
      throw new Error('PDF blob is empty or invalid')
    }
    
    console.log(`✅ PDF blob généré (${Math.round(pdfBlob.size / 1024)}KB)`)
    console.log('🔍 Blob type:', pdfBlob.type)
    
    // Try primary conversion method first
    let pdfBase64
    try {
      pdfBase64 = await convertBlobToBase64(pdfBlob)
      console.log('✅ Conversion primaire réussie')
    } catch (primaryError) {
      console.warn('⚠️ Conversion primaire échouée, tentative alternative:', primaryError.message)
      
      // Fallback to alternative method
      try {
        pdfBase64 = await convertBlobToBase64Alternative(pdfBlob)
        console.log('✅ Conversion alternative réussie')
      } catch (altError) {
        console.error('❌ Toutes les méthodes de conversion ont échoué')
        throw new Error(`Both conversion methods failed. Primary: ${primaryError.message}, Alternative: ${altError.message}`)
      }
    }
    
    console.log(`✅ PDF converti en base64 (${Math.round(pdfBase64.length / 1024)}KB)`)
    
    return {
      blob: pdfBlob,
      base64: pdfBase64,
      size: pdfBlob.size,
      sizeKB: Math.round(pdfBlob.size / 1024)
    }
    
  } catch (error) {
    console.error('❌ Erreur génération PDF blob:', error)
    throw new Error(`PDF generation failed: ${error.message}`)
  }
}

/**
 * Send PDF blob to backend via API
 * @param {string} endpoint - API endpoint
 * @param {Object} data - Request data
 * @param {string} pdfBase64 - Base64 encoded PDF
 * @returns {Promise<Object>} - API response
 */
export const sendPdfToBackend = async (endpoint, data, pdfBase64) => {
  try {
    console.log(`📤 Envoi PDF vers ${endpoint}...`)
    
    const requestBody = {
      ...data,
      pdfBlob: pdfBase64
    }
    
    const response = await fetch(endpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(requestBody)
    })
    
    if (!response.ok) {
      const errorData = await response.json()
      throw new Error(errorData.error?.message || `HTTP ${response.status}`)
    }
    
    const responseData = await response.json()
    console.log(`✅ PDF envoyé avec succès vers ${endpoint}`)
    
    return responseData
    
  } catch (error) {
    console.error(`❌ Erreur envoi PDF vers ${endpoint}:`, error)
    throw error
  }
}

/**
 * Complete PDF generation and backend transmission workflow
 * @param {React.Component} pdfComponent - React-PDF component
 * @param {string} endpoint - API endpoint
 * @param {Object} data - Request data (clientInfo, assessmentData, etc.)
 * @returns {Promise<Object>} - API response
 */
export const generateAndSendPdf = async (pdfComponent, endpoint, data) => {
  try {
    console.log('🔄 Début du workflow PDF complet...')
    
    // Step 1: Generate PDF blob and convert to base64
    const pdfResult = await generatePdfBlobAndBase64(pdfComponent)
    
    // Step 2: Send to backend
    const apiResponse = await sendPdfToBackend(endpoint, data, pdfResult.base64)
    
    console.log('✅ Workflow PDF terminé avec succès')
    
    return {
      success: true,
      pdf: pdfResult,
      response: apiResponse
    }
    
  } catch (error) {
    console.error('❌ Erreur workflow PDF:', error)
    
    return {
      success: false,
      error: error.message,
      pdf: null,
      response: null
    }
  }
}

/**
 * Validate PDF blob before processing
 * @param {Blob} pdfBlob - PDF blob to validate
 * @returns {Object} - Validation result
 */
export const validatePdfBlob = (pdfBlob) => {
  try {
    if (!pdfBlob) {
      return { isValid: false, error: 'PDF blob is null or undefined' }
    }
    
    if (!(pdfBlob instanceof Blob)) {
      return { isValid: false, error: 'Not a valid Blob object' }
    }
    
    if (pdfBlob.size === 0) {
      return { isValid: false, error: 'PDF blob is empty' }
    }
    
    if (pdfBlob.type && pdfBlob.type !== 'application/pdf') {
      console.warn(`⚠️ Unexpected blob type: ${pdfBlob.type}`)
    }
    
    // Check minimum size (should be at least a few KB for a valid PDF)
    if (pdfBlob.size < 1000) {
      return { isValid: false, error: 'PDF blob is too small to be valid' }
    }
    
    return {
      isValid: true,
      size: pdfBlob.size,
      sizeKB: Math.round(pdfBlob.size / 1024),
      type: pdfBlob.type
    }
    
  } catch (error) {
    return { isValid: false, error: error.message }
  }
}
